import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';

// Manual track matching is an occasional workflow, so its markup and search
// logic stay out of the initial bundle until someone actually opens it.
const TrackSearchModal = lazy(() => import('../components/TrackSearchModal'));
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from '../utils';

function safeHref(url) {
  try {
    const u = new URL(url);
    return (u.protocol === 'http:' || u.protocol === 'https:') ? url : null;
  } catch { return null; }
}

export default function History() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  // Plex, Jellyfin and Navidrome behave identically, so they are keyed by server
  // name rather than given a parallel set of hooks and handlers each.
  //   serverSync     { plex: { [playlistId]: { loading, result, error } }, ... }
  //   serverSyncAll  { plex: { loading, result }, ... }
  const [serverSync, setServerSync] = useState({});
  // { plex: { "<playlistId>": { done, total } }, ... } while a sync runs
  const [syncProgress, setSyncProgress] = useState({});
  const [serverSyncAll, setServerSyncAll] = useState({});
  const [spotifyPushStates, setSpotifyPushStates] = useState({});  // { [id]: { loading, result, error } }
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [jellyfinConfigured, setJellyfinConfigured] = useState(false);
  const [navidromeConfigured, setNavidromeConfigured] = useState(false);
  const [refreshStates, setRefreshStates] = useState({});  // { [id]: { loading, result, error } }
  // Derived views onto the two states above, so the render keeps reading the
  // same names it always has.
  const syncStates = serverSync.plex || {};
  const jellyfinSyncStates = serverSync.jellyfin || {};
  const navidromeSyncStates = serverSync.navidrome || {};
  const syncAllLoading = !!(serverSyncAll.plex && serverSyncAll.plex.loading);
  const syncAllResult = (serverSyncAll.plex && serverSyncAll.plex.result) || null;
  const jellyfinSyncAllLoading = !!(serverSyncAll.jellyfin && serverSyncAll.jellyfin.loading);
  const jellyfinSyncAllResult = (serverSyncAll.jellyfin && serverSyncAll.jellyfin.result) || null;
  const navidromeSyncAllLoading = !!(serverSyncAll.navidrome && serverSyncAll.navidrome.loading);
  const navidromeSyncAllResult = (serverSyncAll.navidrome && serverSyncAll.navidrome.result) || null;
  const [confirmDelete, setConfirmDelete] = useState(null); // playlist id pending delete
  const [confirmRefresh, setConfirmRefresh] = useState(null); // playlist id pending refresh
  const [renamingId, setRenamingId] = useState(null); // playlist id being renamed
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null); // overflow menu open for playlist id
  const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [globalMerge, setGlobalMerge] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  // Which artist list an expanded row is showing: { [playlistId]: 'last' | 'all' }.
  // Defaults to the last refresh so the panel agrees with the row's badge.
  const [chipView, setChipView] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('digarr_history_collapsed') || '{"static":true}');
    } catch {
      return { static: true };
    }
  });
  const [importJobs, setImportJobs] = useState([]);
  const completedJobIds = useRef(new Set());
  const dismissedJobIds = useRef(new Set());

  // Manual track matching
  const [searchModal, setSearchModal] = useState(null); // { track, playlistId }
  const [manualMatches, setManualMatches] = useState({}); // { "artist||title" → matched track }
  const [ignoredTracks, setIgnoredTracks] = useState(new Set()); // Set of "artist_lower||title_lower"
  const [expandedIgnored, setExpandedIgnored] = useState({}); // { playlistId → bool }
  const [expandedBadge, setExpandedBadge] = useState({}); // { plId: 'plex'|'jellyfin'|'navidrome'|null }
  const [trackDetails, setTrackDetails] = useState({}); // { plId: { tracks, plex_unmatched_tracks } }

  // Close overflow menu on any outside click
  useEffect(() => {
    if (!menuOpenId) return;
    const handler = () => setMenuOpenId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuOpenId]);

  useEffect(() => {
    axios.get('/api/spotify/status').then(r => setSpotifyConnected(r.data.connected)).catch(() => {});

    axios.get('/api/jellyfin/status').then(r => setJellyfinConfigured(r.data.configured)).catch(() => {});
    axios.get('/api/navidrome/status').then(r => setNavidromeConfigured(r.data.configured)).catch(() => {});
    axios.get('/api/config').then(r => {
      setTimezone(r.data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
      setGlobalMerge(r.data.refresh_merge_tracks || false);
    }).catch(() => {});
    axios.get('/api/playlists').then(r => {
      setPlaylists(r.data.playlists);
      setLoading(false);
    }).catch(() => setLoading(false));
    // Restore loading state for any refreshes that were running before page load
    axios.get('/api/playlists/refresh/running').then(r => {
      const running = r.data.running || [];
      if (running.length) {
        setRefreshStates(prev => {
          const next = { ...prev };
          running.forEach(id => { next[id] = { loading: true, result: null, error: null }; });
          return next;
        });
        running.forEach(id => pollRefreshJob(id));
      }
    }).catch(() => {});
    axios.get('/api/import/jobs').then(r => setImportJobs((r.data.jobs || []).filter(j => !dismissedJobIds.current.has(j.id)))).catch(() => {});
    axios.get('/api/library/ignored-tracks').then(r => {
      const keys = new Set((r.data.ignored || []).map(t => `${t.artist.toLowerCase()}||${t.title.toLowerCase()}`));
      setIgnoredTracks(keys);
    }).catch(() => {});
  }, []);

  // Poll job progress while any job is running/queued
  useEffect(() => {
    const active = importJobs.filter(j => j.status === 'queued' || j.status === 'running');
    if (!active.length) return;
    const activeIds = new Set(active.map(j => j.id));

    // Immediately reload playlists so newly-queued imports appear without waiting for completion
    axios.get('/api/playlists').then(r => setPlaylists(r.data.playlists)).catch(() => {});

    const timer = setInterval(async () => {
      try {
        const res = await axios.get('/api/import/jobs');
        const newJobs = res.data.jobs || [];

        // When a job we were watching just finished, reload the playlist list
        const newlyDone = newJobs.filter(j =>
          activeIds.has(j.id) && j.status === 'done' && !completedJobIds.current.has(j.id)
        );
        if (newlyDone.length > 0) {
          newlyDone.forEach(j => completedJobIds.current.add(j.id));
          axios.get('/api/playlists').then(r => setPlaylists(r.data.playlists)).catch(() => {});
        }

        setImportJobs(newJobs.filter(j => !dismissedJobIds.current.has(j.id)));
      } catch {}
    }, 2000);

    return () => clearInterval(timer);
  }, [importJobs]);

  // Sweeps every refreshable playlist immediately, regardless of its own
  // cadence. Per-playlist state lands on the rows themselves, so there is no
  // separate summary panel to feed.
  const handleRefreshAll = async () => {
    setRunningNow(true);
    try {
      await axios.post('/api/scheduler/run-now');
      const r = await axios.get('/api/playlists');
      setPlaylists(r.data.playlists);
    } catch {
      /* row-level errors surface on the rows */
    }
    setRunningNow(false);
  };

  const openSearchModal = (track, playlistId) => setSearchModal({ track, playlistId });

  const handleIgnoreTrack = async (track) => {
    const key = `${(track.artist || '').toLowerCase()}||${(track.title || '').toLowerCase()}`;
    setIgnoredTracks(prev => new Set([...prev, key]));
    try {
      await axios.post('/api/library/ignore-track', { artist: track.artist || '', title: track.title || '' });
    } catch {
      setIgnoredTracks(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const handleUnignoreTrack = async (track) => {
    const key = `${(track.artist || '').toLowerCase()}||${(track.title || '').toLowerCase()}`;
    setIgnoredTracks(prev => { const n = new Set(prev); n.delete(key); return n; });
    try {
      await axios.delete('/api/library/ignore-track', { data: { artist: track.artist || '', title: track.title || '' } });
    } catch {
      setIgnoredTracks(prev => new Set([...prev, key]));
    }
  };

  const handleToggleBadge = async (e, pl, source) => {
    e.stopPropagation();
    const current = expandedBadge[pl.id];
    if (current === source) {
      setExpandedBadge(prev => ({ ...prev, [pl.id]: null }));
      return;
    }
    setExpandedBadge(prev => ({ ...prev, [pl.id]: source }));
    if (!trackDetails[pl.id]) {
      try {
        const res = await axios.get(`/api/playlists/${pl.id}`);
        setTrackDetails(prev => ({ ...prev, [pl.id]: res.data }));
      } catch {}
    }
  };


const handlePushToSpotify = async (pl) => {
    setSpotifyPushStates(prev => ({ ...prev, [pl.id]: { loading: true, result: null, error: null } }));
    try {
      const res = await axios.post(`/api/spotify/push/${pl.id}`);
      setSpotifyPushStates(prev => ({ ...prev, [pl.id]: { loading: false, result: res.data, error: null } }));
      setPlaylists(prev => prev.map(p =>
        p.id === pl.id ? { ...p, spotify_playlist_id: res.data.playlist_id } : p
      ));
    } catch (err) {
      setSpotifyPushStates(prev => ({ ...prev, [pl.id]: { loading: false, result: null, error: err.response?.data?.detail || 'Spotify push failed.' } }));
    }
  };

  const handleDownloadM3U = (pl) => {
    window.open(`/api/playlists/${pl.id}/m3u`, '_blank');
  };

  const handleDownloadJSPF = (pl) => {
    window.open(`/api/playlists/${pl.id}/jspf`, '_blank');
  };

  // A media-server sync makes one search per uncached track, which can take
  // tens of seconds. Poll the backend for its progress so the button can show
  // real counts rather than an indefinite spinner. Polling only runs while a
  // sync is in flight, and stops as soon as the last one finishes.
  const anySyncLoading = Object.values(serverSync).some(
    byId => Object.values(byId || {}).some(s => s && s.loading)
  );

  useEffect(() => {
    if (!anySyncLoading) {
      setSyncProgress({});
      return undefined;
    }
    let alive = true;
    const tick = async () => {
      try {
        const r = await axios.get('/api/sync/progress');
        if (alive) setSyncProgress(r.data || {});
      } catch {
        /* transient failures are not worth surfacing mid-sync */
      }
    };
    tick();
    const handle = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(handle); };
  }, [anySyncLoading]);

  const syncingLabel = (server, pl) => {
    const p = (syncProgress[server] || {})[String(pl.id)];
    return p && p.total ? `Syncing ${p.done}/${p.total}` : 'Syncing\u2026';
  };

  const setSyncState = (server, playlistId, state) =>
    setServerSync(prev => ({ ...prev, [server]: { ...(prev[server] || {}), [playlistId]: state } }));

  // Every server exposes the same contract: POST /api/<server>/playlist/<id>/sync
  // returning { <server>_playlist_id, matched, total }.
  const handleSyncServer = async (e, pl, server, label) => {
    e.stopPropagation();
    setSyncState(server, pl.id, { loading: true, result: null, error: null });
    try {
      const res = await axios.post(`/api/${server}/playlist/${pl.id}/sync`);
      setSyncState(server, pl.id, { loading: false, result: res.data, error: null });
      setPlaylists(prev => prev.map(p => p.id === pl.id ? {
        ...p,
        [`${server}_playlist_id`]: res.data[`${server}_playlist_id`],
        [`${server}_matched_count`]: res.data.matched,
        [`${server}_total_count`]: res.data.total,
      } : p));
    } catch (err) {
      setSyncState(server, pl.id, {
        loading: false,
        result: null,
        error: err.response?.data?.detail || `${label} sync failed.`,
      });
    }
  };

  const handleSyncPlex = (e, pl) => handleSyncServer(e, pl, 'plex', 'Plex');
  const handleSyncJellyfin = (e, pl) => handleSyncServer(e, pl, 'jellyfin', 'Jellyfin');
  const handleSyncNavidrome = (e, pl) => handleSyncServer(e, pl, 'navidrome', 'Navidrome');

  const pollRefreshJob = (playlistId, plRef) => {
    const poll = async () => {
      try {
        const res = await axios.get(`/api/playlists/${playlistId}/refresh/status`);
        if (res.data.status === 'running') { setTimeout(poll, 2000); return; }
        if (res.data.status === 'error') throw new Error(res.data.error || 'Refresh failed.');
        const data = res.data.result;
        const pl = plRef || playlists.find(p => p.id === playlistId) || { id: playlistId };
        let mediaUpdate = {};
        const ps = data.plex_sync;
        if (ps) {
          mediaUpdate = { ...mediaUpdate, plex_playlist_id: ps.plex_playlist_id ?? pl.plex_playlist_id, plex_matched_count: ps.matched, plex_total_count: ps.total, plex_unmatched_tracks: ps.unmatched ?? [] };
          setSyncState('plex', playlistId, { loading: false, result: ps, error: null });
        }
        const js = data.jellyfin_sync;
        if (js) {
          mediaUpdate = { ...mediaUpdate, jellyfin_matched_count: js.matched, jellyfin_total_count: js.total };
          setSyncState('jellyfin', playlistId, { loading: false, result: js, error: null });
        }
        const ns = data.navidrome_sync;
        if (ns) {
          mediaUpdate = { ...mediaUpdate, navidrome_matched_count: ns.matched, navidrome_total_count: ns.total };
          setSyncState('navidrome', playlistId, { loading: false, result: ns, error: null });
        }
        setRefreshStates(prev => ({ ...prev, [playlistId]: { loading: false, result: data, error: null } }));
        setPlaylists(prev => prev.map(p => p.id === playlistId ? { ...p, last_refreshed_at: new Date().toISOString(), ...mediaUpdate } : p));
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Refresh failed.';
        setRefreshStates(prev => ({ ...prev, [playlistId]: { loading: false, result: null, error: msg } }));
      }
    };
    poll();
  };

  const handleRefresh = async (e, pl) => {
    e.stopPropagation();
    if (confirmRefresh !== pl.id) {
      setConfirmRefresh(pl.id);
      return;
    }
    setConfirmRefresh(null);
    setRefreshStates(prev => ({ ...prev, [pl.id]: { loading: true, result: null, error: null } }));
    try {
      await axios.post(`/api/playlists/${pl.id}/refresh`);
      pollRefreshJob(pl.id, pl);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Refresh failed.';
      setRefreshStates(prev => ({ ...prev, [pl.id]: { loading: false, result: null, error: msg } }));
    }
  };

  const handleDelete = async (e, pl) => {
    e.stopPropagation();
    if (confirmDelete === pl.id) {
      try {
        await axios.delete(`/api/playlists/${pl.id}`);
        setPlaylists(prev => prev.filter(p => p.id !== pl.id));
        if (selected?.id === pl.id) setSelected(null);
      } catch {
        // deletion failed — leave playlist in list
      }
      setConfirmDelete(null);
    } else {
      setConfirmDelete(pl.id);
    }
  };

  const REFRESHABLE_TYPES = ['url', 'm3u_url', 'listenbrainz', 'similar', 'spotify'];

  // A playlist is "dynamic" when it is both refreshable and not pinned to never.
  // Derived from the cadence rather than a separate flag, so the group a
  // playlist sits in can never disagree with whether it actually refreshes.
  const isDynamic = pl => {
    if (!pl.source_url || !REFRESHABLE_TYPES.includes(pl.source_type)) return false;
    const pin = pl.refresh_pin_hours;
    return !(pin !== null && pin !== undefined && Number(pin) <= 0);
  };

  const formatHours = h => {
    const n = Number(h);
    if (!n || n <= 0) return null;
    if (n < 1) return `${Math.round(n * 60)}m`;
    return n >= 10 || Number.isInteger(n) ? `${Math.round(n)}h` : `${n.toFixed(1)}h`;
  };

  const cadenceLabel = pl => {
    const pin = pl.refresh_pin_hours;
    if (pin !== null && pin !== undefined) {
      return Number(pin) <= 0 ? 'Never' : `Every ${formatHours(pin)}`;
    }
    const cur = formatHours(pl.refresh_interval_hours);
    return cur ? `Adaptive · ~${cur}` : 'Adaptive';
  };

  const nextRefreshLabel = pl => {
    if (!pl.next_refresh_at || !isDynamic(pl)) return null;
    const ms = new Date(pl.next_refresh_at).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    if (ms <= 0) return 'due now';
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `in ${mins}m`;
    const hrs = mins / 60;
    return hrs < 24 ? `in ${hrs.toFixed(hrs < 10 ? 1 : 0)}h` : `in ${Math.round(hrs / 24)}d`;
  };

  const toggleGroup = key => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('digarr_history_collapsed', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const handleSetCadence = async (e, pl, value) => {
    e.stopPropagation();
    const body = value === 'adaptive' ? { adaptive: true } : { pin_hours: Number(value) };
    try {
      const r = await axios.post(`/api/playlists/${pl.id}/set-refresh`, body);
      setPlaylists(prev => prev.map(p => p.id === pl.id ? {
        ...p,
        refresh_pin_hours: r.data.pin_hours,
        refresh_interval_hours: r.data.interval_hours,
        next_refresh_at: r.data.next_refresh_at,
      } : p));
    } catch {
      /* leave the previous value in place on failure */
    }
  };

  const handleSetMergeTracks = async (e, pl, value) => {
    e.stopPropagation();
    await axios.post(`/api/playlists/${pl.id}/set-merge-tracks`, { merge_tracks: value });
    setPlaylists(prev => prev.map(p => p.id === pl.id ? { ...p, merge_tracks: value } : p));
    if (selected?.id === pl.id) setSelected(prev => ({ ...prev, merge_tracks: value }));
  };

  const handleSyncAllServer = async (server, label) => {
    setServerSyncAll(prev => ({ ...prev, [server]: { loading: true, result: null } }));
    try {
      const res = await axios.post(`/api/${server}/sync-all`);
      setServerSyncAll(prev => ({ ...prev, [server]: { loading: false, result: res.data } }));
      setPlaylists(prev => prev.map(p => {
        const r = res.data.results?.find(x => x.id === p.id);
        if (!r || r.status !== 'ok') return p;
        const patch = {
          [`${server}_playlist_id`]: r[`${server}_playlist_id`] ?? p[`${server}_playlist_id`],
          [`${server}_matched_count`]: r.matched,
          [`${server}_total_count`]: r.total,
        };
        // Only Plex reports per-track misses back to the list view.
        if (server === 'plex') patch.plex_unmatched_tracks = r.unmatched ?? p.plex_unmatched_tracks;
        return { ...p, ...patch };
      }));
    } catch (err) {
      setServerSyncAll(prev => ({
        ...prev,
        [server]: { loading: false, result: { error: err.response?.data?.detail || `${label} sync all failed.` } },
      }));
    }
  };

  const handleSyncAll = () => handleSyncAllServer('plex', 'Plex');
  const handleSyncAllJellyfin = () => handleSyncAllServer('jellyfin', 'Jellyfin');
  const handleSyncAllNavidrome = () => handleSyncAllServer('navidrome', 'Navidrome');

  const startRename = (e, pl) => {
    e.stopPropagation();
    setRenamingId(pl.id);
    setRenameValue(displayName(pl.name));
    setRenameError(null);
  };

  const commitRename = async (pl) => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === pl.name) {
      setRenamingId(null);
      return;
    }
    try {
      await axios.put(`/api/playlists/${pl.id}/name`, { name: trimmed });
      setPlaylists(prev => prev.map(p => p.id === pl.id ? { ...p, name: trimmed } : p));
      if (selected?.id === pl.id) setSelected(prev => ({ ...prev, name: trimmed }));
      setRenamingId(null);
      setRenameError(null);
    } catch (err) {
      setRenameError(err.response?.data?.detail || 'Rename failed.');
    }
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameError(null);
  };

  const fmt = (iso) => formatDate(iso, timezone);

  // Strip any "— Digarr" / "-- Digarr" / "- Digarr" suffix from display names.
  // The suffix belongs in Plex only (plex_playlist_name); some older playlists have
  // it baked into the name column. We strip it at render time without touching the DB.
  const displayName = (name) => name.replace(/\s+[\u2014\-]{1,2}\s*Digarr\s*$/i, '').trim();

  const sourceIcon = (type) => {
    switch (type) {
      case 'url': return '🌐';
      case 'm3u_url': return '📡';
      case 'file': return '📁';
      case 'text': return '📝';
      default: return '⦿';
    }
  };

  if (loading) {
    return (
      <div>
        <h1 className="page-title">History</h1>
        <p className="page-subtitle">All your past imports</p>
        <div className="empty"><span className="spinner" style={{ width: 32, height: 32 }} /></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-title">History</h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <p className="page-subtitle" style={{ margin: 0 }}>All your past imports — {playlists.length} total</p>
        <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={handleRefreshAll} disabled={runningNow}>
          {runningNow ? <><span className="spinner" style={{ width: 10, height: 10 }} /> Refreshing…</> : '⟳ Refresh All'}
        </button>
        {jellyfinConfigured && playlists.some(p => p.jellyfin_playlist_id) && (
          <button className="btn btn-ghost" style={{ fontSize: 11, color: '#00a4dc', borderColor: '#00a4dc' }}
            disabled={jellyfinSyncAllLoading} onClick={handleSyncAllJellyfin}>
            {jellyfinSyncAllLoading ? <><span className="spinner" style={{ width: 10, height: 10 }} /> Syncing…</> : '⟳ Sync All to Jellyfin'}
          </button>
        )}
        {navidromeConfigured && playlists.some(p => p.navidrome_playlist_id) && (
          <button className="btn btn-ghost" style={{ fontSize: 11, color: '#fc6e51', borderColor: '#fc6e51' }}
            disabled={navidromeSyncAllLoading} onClick={handleSyncAllNavidrome}>
            {navidromeSyncAllLoading ? <><span className="spinner" style={{ width: 10, height: 10 }} /> Syncing…</> : '⟳ Sync All to Navidrome'}
          </button>
        )}
      </div>
      {syncAllResult && !syncAllResult.error && (
        <div style={{ marginBottom: '1rem', fontSize: 12, display: 'grid', gap: '0.2rem' }}>
          {syncAllResult.results?.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ color: r.status === 'error' ? 'var(--red)' : 'var(--text-muted)' }}>
                {r.status === 'error' ? '✕' : '✓'}
              </span>
              <span>{r.name}</span>
              {r.status === 'ok' && (
                <span className="text-muted">{r.matched}/{r.total} matched</span>
              )}
              {r.status === 'error' && (
                <span style={{ color: 'var(--red)', fontSize: 11 }}>{r.error}</span>
              )}
            </div>
          ))}
        </div>
      )}
      {syncAllResult?.error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{syncAllResult.error}</div>
      )}
      {jellyfinSyncAllResult && !jellyfinSyncAllResult.error && (
        <div style={{ marginBottom: '1rem', fontSize: 12, display: 'grid', gap: '0.2rem' }}>
          {jellyfinSyncAllResult.results?.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ color: r.status === 'error' ? 'var(--red)' : 'var(--text-muted)' }}>{r.status === 'error' ? '✕' : '✓'}</span>
              <span>{r.name}</span>
              {r.status === 'ok' && <span className="text-muted">{r.matched}/{r.total} matched</span>}
              {r.status === 'error' && <span style={{ color: 'var(--red)', fontSize: 11 }}>{r.error}</span>}
            </div>
          ))}
        </div>
      )}
      {jellyfinSyncAllResult?.error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{jellyfinSyncAllResult.error}</div>}
      {navidromeSyncAllResult && !navidromeSyncAllResult.error && (
        <div style={{ marginBottom: '1rem', fontSize: 12, display: 'grid', gap: '0.2rem' }}>
          {navidromeSyncAllResult.results?.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ color: r.status === 'error' ? 'var(--red)' : 'var(--text-muted)' }}>{r.status === 'error' ? '✕' : '✓'}</span>
              <span>{r.name}</span>
              {r.status === 'ok' && <span className="text-muted">{r.matched}/{r.total} matched</span>}
              {r.status === 'error' && <span style={{ color: 'var(--red)', fontSize: 11 }}>{r.error}</span>}
            </div>
          ))}
        </div>
      )}
      {navidromeSyncAllResult?.error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{navidromeSyncAllResult.error}</div>}

      {/* Active import jobs */}
      {importJobs.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-title" style={{ marginBottom: '0.9rem' }}>Imports</div>
          {importJobs.map(job => {
            const isDone = job.status === 'done';
            const isError = job.status === 'error';
            const pct = job.total > 0 ? Math.round((job.current / job.total) * 100) : 0;
            const added = isDone ? (job.results || []).filter(r => r.status === 'added').length : 0;
            const errors = isDone ? (job.results || []).filter(r => r.status === 'error').length : 0;
            return (
              <div key={job.id} style={{ marginBottom: '0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                    <span style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.playlist_name}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                    <span style={{ fontSize: 11, color: isDone ? 'var(--green)' : isError ? 'var(--red)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {isDone
                        ? `✓ ${added} new artist${added !== 1 ? 's' : ''}${errors > 0 ? ` · ${errors} failed` : ''}${job.plex_result ? ` · Plex: ${job.plex_result.matched}/${job.plex_result.total}` : ''}${job.jellyfin_result ? ` · Jellyfin: ${job.jellyfin_result.matched}/${job.jellyfin_result.total}` : ''}${job.navidrome_result ? ` · Navidrome: ${job.navidrome_result.matched}/${job.navidrome_result.total}` : ''}${job.deemix_result ? ` · Deemix: ${job.deemix_result.queued}/${job.deemix_result.total}` : ''}`
                        : isError ? `Error: ${job.error}`
                        : job.status === 'queued' ? 'Queued…'
                        : `${job.current}/${job.total}${job.current_artist ? `: ${job.current_artist}` : ''}`}
                    </span>
                    {(isDone || isError) && (
                      <button className="btn btn-ghost" style={{ fontSize: 10, padding: '1px 6px' }}
                        onClick={() => {
                          dismissedJobIds.current.add(job.id);
                          axios.delete(`/api/import/jobs/${job.id}`).catch(() => {});
                          setImportJobs(prev => prev.filter(j => j.id !== job.id));
                        }}>
                        ✕
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${isDone || isError ? 100 : pct}%`,
                    background: isDone ? 'var(--green)' : isError ? 'var(--red)' : 'var(--accent)',
                    borderRadius: 3,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {playlists.length === 0 ? (
        <div className="empty">
          <span className="empty-icon">⦿</span>
          No digs yet.{' '}
          <Link to="/" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Head to Import to get started →</Link>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {[
            { key: 'dynamic', label: 'Auto-refreshing', items: playlists.filter(isDynamic) },
            { key: 'static', label: 'Not refreshing', items: playlists.filter(p => !isDynamic(p)) },
          ].map(group => group.items.length === 0 ? null : (
            <div key={group.key}>
              <div
                onClick={() => toggleGroup(group.key)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer',
                         userSelect: 'none', padding: '0.35rem 0', marginBottom: '0.35rem',
                         fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em',
                         color: 'var(--text-muted)' }}>
                <span style={{ display: 'inline-block', transition: 'transform 0.15s',
                               transform: collapsedGroups[group.key] ? 'rotate(0deg)' : 'rotate(90deg)' }}>›</span>
                {group.label} ({group.items.length})
              </div>
              {!collapsedGroups[group.key] && (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {group.items.map(pl => {
            const sync = syncStates[pl.id] || {};
            const jellyfinSync = jellyfinSyncStates[pl.id] || {};
            const navidromeSync = navidromeSyncStates[pl.id] || {};
            const refresh = refreshStates[pl.id] || {};
            const spotifyPush = spotifyPushStates[pl.id] || {};
            const canRefresh = pl.source_url && ['url', 'm3u_url', 'listenbrainz', 'similar', 'spotify'].includes(pl.source_type);
            const activeJob = importJobs.find(j => j.playlist_id === pl.id && (j.status === 'queued' || j.status === 'running'));
            return (
              <div key={pl.id} className="card">
                <div className="flex-between" style={{ cursor: 'pointer', borderRadius: 6, margin: '-0.25rem', padding: '0.25rem', transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => setSelected(selected?.id === pl.id ? null : pl)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ color: 'var(--accent)', fontSize: 29, fontWeight: 700, transition: 'transform 0.15s', display: 'inline-block', transform: selected?.id === pl.id ? 'rotate(90deg)' : 'rotate(0deg)', userSelect: 'none', lineHeight: 1 }}>›</span>
                      {sourceIcon(pl.source_type)}{' '}
                      {renamingId === pl.id ? (
                        <span onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') commitRename(pl);
                              if (e.key === 'Escape') cancelRename();
                            }}
                            style={{
                              background: 'var(--input-bg, rgba(255,255,255,0.08))',
                              border: '1px solid var(--accent)',
                              borderRadius: 4,
                              color: 'var(--text)',
                              padding: '2px 6px',
                              fontSize: 14,
                              fontWeight: 600,
                              width: 260,
                            }}
                          />
                          <button className="btn btn-ghost" style={{ fontSize: 10, color: 'var(--accent)', borderColor: 'var(--accent)' }}
                            onClick={e => { e.stopPropagation(); commitRename(pl); }}>Save</button>
                          <button className="btn btn-ghost" style={{ fontSize: 10 }}
                            onClick={e => { e.stopPropagation(); cancelRename(); }}>Cancel</button>
                          {renameError && <span style={{ fontSize: 11, color: 'var(--red)', marginLeft: 4 }}>{renameError}</span>}
                        </span>
                      ) : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          {displayName(pl.name)}
                          <span
                            title="Rename"
                            onClick={e => startRename(e, pl)}
                            style={{ cursor: 'pointer', opacity: 0.4, fontSize: 11, lineHeight: 1 }}
                            onMouseEnter={e => e.currentTarget.style.opacity = 1}
                            onMouseLeave={e => e.currentTarget.style.opacity = 0.4}
                          >✎</span>
                        </span>
                      )}
                    </div>
                    <div className="text-muted text-mono">
                      {fmt(pl.created_at)}
                      {safeHref(pl.source_url) && (
                        <> · <a href={safeHref(pl.source_url)} target="_blank" rel="noreferrer"
                          style={{ color: 'var(--accent)', textDecoration: 'none' }}
                          onClick={e => e.stopPropagation()}>
                          source ↗
                        </a></>
                      )}
                    </div>
                  </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {activeJob ? (
                      <span className="badge" style={{ background: 'rgba(var(--accent-rgb,99,102,241),0.15)', color: 'var(--accent)', fontSize: 10, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span className="spinner" style={{ width: 8, height: 8 }} /> Importing…
                      </span>
                    ) : (() => {
                      const newCount = pl.last_refresh_new_artists != null
                        ? pl.last_refresh_new_artists.length
                        : (pl.artists_added?.length || 0);
                      const tooltip = pl.last_refresh_new_artists != null
                        ? `${newCount} new artist${newCount !== 1 ? 's' : ''} found in last refresh`
                        : `${newCount} new artist${newCount !== 1 ? 's' : ''} added on import`;
                      return (
                        <span className="badge badge-added" title={tooltip} style={{ cursor: 'default' }}>{newCount} new artists</span>
                      );
                    })()}
                    {pl.plex_playlist_id && (
                      <span className="badge" style={{ background: 'var(--accent)', color: '#fff', fontSize: 10, cursor: 'pointer', userSelect: 'none' }}
                        title={pl.plex_matched_count != null ? `${pl.plex_matched_count} of ${pl.plex_total_count} tracks matched in Plex — click to see` : 'Playlist exists in Plex'}
                        onClick={e => handleToggleBadge(e, pl, 'plex')}>
                        {pl.plex_matched_count != null
                          ? `Plex: ${pl.plex_matched_count}/${pl.plex_total_count} tracks`
                          : 'In Plex'}
                        {' '}{expandedBadge[pl.id] === 'plex' ? '▲' : '▼'}
                      </span>
                    )}
                    {pl.jellyfin_playlist_id && (
                      <span className="badge" style={{ background: '#00a4dc', color: '#fff', fontSize: 10, cursor: 'pointer', userSelect: 'none' }}
                        title={pl.jellyfin_matched_count != null ? `${pl.jellyfin_matched_count} of ${pl.jellyfin_total_count} tracks matched in Jellyfin — click to see` : 'Playlist exists in Jellyfin'}
                        onClick={e => handleToggleBadge(e, pl, 'jellyfin')}>
                        {pl.jellyfin_matched_count != null
                          ? `Jellyfin: ${pl.jellyfin_matched_count}/${pl.jellyfin_total_count}`
                          : 'In Jellyfin'}
                        {' '}{expandedBadge[pl.id] === 'jellyfin' ? '▲' : '▼'}
                      </span>
                    )}
                    {pl.navidrome_playlist_id && (
                      <span className="badge" style={{ background: '#fc6e51', color: '#fff', fontSize: 10, cursor: 'pointer', userSelect: 'none' }}
                        title={pl.navidrome_matched_count != null ? `${pl.navidrome_matched_count} of ${pl.navidrome_total_count} tracks matched in Navidrome — click to see` : 'Playlist exists in Navidrome'}
                        onClick={e => handleToggleBadge(e, pl, 'navidrome')}>
                        {pl.navidrome_matched_count != null
                          ? `Navidrome: ${pl.navidrome_matched_count}/${pl.navidrome_total_count}`
                          : 'In Navidrome'}
                        {' '}{expandedBadge[pl.id] === 'navidrome' ? '▲' : '▼'}
                      </span>
                    )}

                    {confirmDelete === pl.id ? (
                      /* Delete confirmation — replaces all other actions */
                      <>
                        <span style={{ fontSize: 11, color: 'var(--red)' }}>Delete?</span>
                        <button className="btn btn-ghost" style={{ fontSize: 10, color: 'var(--red)', borderColor: 'var(--red)' }}
                          onClick={e => handleDelete(e, pl)}>
                          Confirm
                        </button>
                        <button className="btn btn-ghost" style={{ fontSize: 10 }}
                          onClick={e => { e.stopPropagation(); setConfirmDelete(null); }}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Refresh — primary action, visible when available */}
                        {canRefresh && (confirmRefresh === pl.id ? (
                          <>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Overwrite?</span>
                            <button className="btn btn-ghost" style={{ fontSize: 10, color: 'var(--accent)', borderColor: 'var(--accent)' }}
                              onClick={e => handleRefresh(e, pl)}>
                              Confirm
                            </button>
                            <button className="btn btn-ghost" style={{ fontSize: 10 }}
                              onClick={e => { e.stopPropagation(); setConfirmRefresh(null); }}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button className="btn btn-ghost" style={{ fontSize: 10, color: 'var(--accent)', borderColor: 'var(--accent)' }}
                            disabled={refresh.loading} onClick={e => handleRefresh(e, pl)}>
                            {refresh.loading
                              ? <><span className="spinner" style={{ width: 10, height: 10 }} /> Refreshing…</>
                              : '↻ Refresh Source'}
                          </button>
                        ))}
                        {refresh.result && (
                          <span style={{ fontSize: 11, color: refresh.result.new_artists?.length > 0 ? 'var(--green)' : 'var(--text-muted)' }}>
                            {refresh.result.new_artists?.length > 0
                              ? `+${refresh.result.new_artists.length} new`
                              : 'Up to date'}
                          </span>
                        )}
                        {refresh.error && <span style={{ fontSize: 11, color: 'var(--red)' }}>{refresh.error}</span>}

                        {/* Sync Plex — primary action */}
                        <button className="btn btn-ghost" style={{ fontSize: 10, color: 'var(--accent)', borderColor: 'var(--accent)' }}
                          disabled={sync.loading || refresh.loading} onClick={e => handleSyncPlex(e, pl)}>
                          {sync.loading
                            ? <><span className="spinner" style={{ width: 10, height: 10 }} /> {syncingLabel('plex', pl)}</>
                            : pl.plex_playlist_id ? '⟳ Sync Plex' : '▶ Push to Plex'}
                        </button>
                        {sync.result && (
                          <span style={{ fontSize: 11, color: sync.result.matched > 0 ? 'var(--green)' : 'var(--text-muted)' }}>
                            {sync.result.matched}/{sync.result.total} matched
                          </span>
                        )}
                        {sync.error && <span style={{ fontSize: 11, color: 'var(--red)' }}>{sync.error}</span>}

                        {/* Spotify push state */}
                        {spotifyPush.loading && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Pushing to Spotify…</span>}
                        {spotifyPush.result && (
                          <span style={{ fontSize: 11, color: 'var(--green)' }}>
                            Spotify: {spotifyPush.result.matched_count}/{spotifyPush.result.total_count} matched
                          </span>
                        )}
                        {spotifyPush.error && <span style={{ fontSize: 11, color: 'var(--red)' }}>{spotifyPush.error}</span>}

{/* Jellyfin — primary button only when already synced */}
                        {jellyfinConfigured && pl.jellyfin_playlist_id && (
                          <button className="btn btn-ghost" style={{ fontSize: 10, color: '#00a4dc', borderColor: '#00a4dc' }}
                            disabled={jellyfinSync.loading} onClick={e => handleSyncJellyfin(e, pl)}>
                            {jellyfinSync.loading
                              ? <><span className="spinner" style={{ width: 10, height: 10 }} /> {syncingLabel('jellyfin', pl)}</>
                              : '⟳ Sync Jellyfin'}
                          </button>
                        )}
                        {jellyfinSync.result && <span style={{ fontSize: 11, color: 'var(--green)' }}>Jellyfin: {jellyfinSync.result.matched}/{jellyfinSync.result.total}</span>}
                        {jellyfinSync.error && <span style={{ fontSize: 11, color: 'var(--red)' }}>{jellyfinSync.error}</span>}

                        {/* Navidrome — primary button only when already synced */}
                        {navidromeConfigured && pl.navidrome_playlist_id && (
                          <button className="btn btn-ghost" style={{ fontSize: 10, color: '#fc6e51', borderColor: '#fc6e51' }}
                            disabled={navidromeSync.loading} onClick={e => handleSyncNavidrome(e, pl)}>
                            {navidromeSync.loading
                              ? <><span className="spinner" style={{ width: 10, height: 10 }} /> {syncingLabel('navidrome', pl)}</>
                              : '⟳ Sync Navidrome'}
                          </button>
                        )}
                        {navidromeSync.result && <span style={{ fontSize: 11, color: 'var(--green)' }}>Navidrome: {navidromeSync.result.matched}/{navidromeSync.result.total}</span>}
                        {navidromeSync.error && <span style={{ fontSize: 11, color: 'var(--red)' }}>{navidromeSync.error}</span>}

                        {/* Overflow menu — M3U download + Delete */}
                        <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                          <button className="btn btn-ghost" style={{ fontSize: 13, padding: '2px 8px', lineHeight: 1 }}
                            onClick={e => { e.stopPropagation(); setMenuOpenId(menuOpenId === pl.id ? null : pl.id); }}>
                            ⋯
                          </button>
                          {menuOpenId === pl.id && (
                            <div style={{
                              position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 100,
                              background: 'var(--card-bg, #1e1e2e)',
                              border: '1px solid var(--border)',
                              borderRadius: 6,
                              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                              minWidth: 140,
                              overflow: 'hidden',
                            }}>
                              <button
                                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'var(--text)', padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                onClick={() => { setMenuOpenId(null); handleDownloadM3U(pl); }}>
                                ↓ Download M3U
                              </button>
                              <button
                                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'var(--text)', padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                onClick={() => { setMenuOpenId(null); handleDownloadJSPF(pl); }}>
                                ↓ Download JSPF
                              </button>
                              {spotifyConnected && (
                                <>
                                  <div style={{ height: 1, background: 'var(--border)', margin: '0 8px' }} />
                                  <button
                                    style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'var(--text)', padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                    onClick={() => { setMenuOpenId(null); handlePushToSpotify(pl); }}>
                                    {pl.spotify_playlist_id ? '⟳ Sync to Spotify' : '▶ Push to Spotify'}
                                  </button>
                                </>
                              )}
                              {jellyfinConfigured && (
                                <>
                                  <div style={{ height: 1, background: 'var(--border)', margin: '0 8px' }} />
                                  <button
                                    style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'var(--text)', padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                    onClick={e => { setMenuOpenId(null); handleSyncJellyfin(e, pl); }}>
                                    {pl.jellyfin_playlist_id ? '⟳ Sync to Jellyfin' : '▶ Push to Jellyfin'}
                                  </button>
                                </>
                              )}
                              {navidromeConfigured && (
                                <>
                                  <div style={{ height: 1, background: 'var(--border)', margin: '0 8px' }} />
                                  <button
                                    style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'var(--text)', padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                    onClick={e => { setMenuOpenId(null); handleSyncNavidrome(e, pl); }}>
                                    {pl.navidrome_playlist_id ? '⟳ Sync to Navidrome' : '▶ Push to Navidrome'}
                                  </button>
                                </>
                              )}
                              <div style={{ height: 1, background: 'var(--border)', margin: '0 8px' }} />
                              <button
                                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'var(--red)', padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                onClick={() => { setMenuOpenId(null); setConfirmDelete(pl.id); }}>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Badge-expanded matched tracks panel */}
                {expandedBadge[pl.id] && (
                  <div onClick={e => e.stopPropagation()} style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                    {!trackDetails[pl.id] ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 12, color: 'var(--text-muted)' }}>
                        <span className="spinner" style={{ width: 12, height: 12 }} /> Loading tracks…
                      </div>
                    ) : (() => {
                      const source = expandedBadge[pl.id];
                      const allTracks = trackDetails[pl.id]?.tracks || [];
                      const syncResult = syncStates[pl.id]?.result;
                      const unmatchedTracks = syncResult?.unmatched ?? pl.plex_unmatched_tracks ?? [];
                      const unmatchedKeys = new Set(
                        unmatchedTracks.map(t => `${(t.artist||'').toLowerCase()}||${(t.title||'').toLowerCase()}`)
                      );
                      // For Plex we have unmatched data; compute matched by exclusion.
                      // For Jellyfin/Navidrome we don't store unmatched, so show all tracks.
                      const hasPlex = source === 'plex' && (pl.plex_unmatched_tracks?.length > 0 || pl.plex_matched_count != null);
                      const matchedTracks = hasPlex
                        ? allTracks.filter(t => !unmatchedKeys.has(`${(t.artist||'').toLowerCase()}||${(t.title||'').toLowerCase()}`))
                        : allTracks;
                      const matchedCount = source === 'plex' ? (pl.plex_matched_count ?? matchedTracks.length)
                        : source === 'jellyfin' ? (pl.jellyfin_matched_count ?? matchedTracks.length)
                        : (pl.navidrome_matched_count ?? matchedTracks.length);
                      const label = source === 'plex' ? 'Plex' : source === 'jellyfin' ? 'Jellyfin' : 'Navidrome';
                      return (
                        <>
                          <div className="text-muted" style={{ fontSize: 11, marginBottom: '0.4rem' }}>
                            {matchedCount} track{matchedCount !== 1 ? 's' : ''} downloaded in {label}
                            {!hasPlex && source !== 'plex' && (
                              <span style={{ marginLeft: 6 }}>(showing all playlist tracks — sync to see per-track match status)</span>
                            )}
                          </div>
                          {matchedTracks.length > 0 ? (
                            <table className="table" style={{ fontSize: 12 }}>
                              <thead>
                                <tr><th>Artist</th><th>Title</th><th>Album</th></tr>
                              </thead>
                              <tbody>
                                {matchedTracks.map((t, i) => (
                                  <tr key={i}>
                                    <td className="text-muted">{t.artist && t.artist !== 'null' ? t.artist : '—'}</td>
                                    <td>{t.title && t.title !== 'null' ? t.title : '—'}</td>
                                    <td className="text-muted">{t.album && t.album !== 'null' ? t.album : <span style={{ opacity: 0.4, fontStyle: 'italic' }}>no album data</span>}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="text-muted" style={{ fontSize: 12 }}>No matched tracks yet.</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Expanded detail */}
                {selected?.id === pl.id && (
                  <div onClick={e => e.stopPropagation()} style={{ marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '1rem', display: 'grid', gap: '1rem' }}>
                    {pl.last_refreshed_at && (
                      <div className="text-muted text-mono" style={{ fontSize: 11 }}>
                        Last refreshed {fmt(pl.last_refreshed_at)}
                      </div>
                    )}
                    {(() => {
                      const results = pl.lidarr_results || [];
                      if (results.length === 0 && !pl.artists_added?.length
                          && !pl.last_refresh_new_artists?.length) return null;

                      // Use full lidarr_results if available, otherwise fall back to artists_added list
                      if (results.length > 0) {
                        const added = results.filter(r => r.status === 'added');
                        const exists = results.filter(r => r.status === 'already_exists');
                        const errors = results.filter(r => r.status === 'error');
                        return (
                          <div>
                            <div className="card-title" style={{ marginBottom: '0.75rem' }}>Lidarr</div>
                            {added.length > 0 && (
                              <div style={{ marginBottom: '0.5rem' }}>
                                <div className="text-muted" style={{ fontSize: 11, marginBottom: '0.3rem' }}>
                                  Added ({added.length})
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                  {added.map((r, i) => (
                                    <span key={i} className="badge badge-added" style={{ fontSize: 11 }}>
                                      {r.artist}{r.album_monitored ? ` — ${r.album_monitored}` : ''}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {exists.length > 0 && (
                              <div style={{ marginBottom: '0.5rem' }}>
                                <div className="text-muted" style={{ fontSize: 11, marginBottom: '0.3rem' }}>
                                  Already in library ({exists.length})
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                  {exists.map((r, i) => (
                                    <span key={i} className="badge badge-exists" style={{ fontSize: 11 }}>{r.artist}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {errors.length > 0 && (
                              <div>
                                <div className="text-muted" style={{ fontSize: 11, marginBottom: '0.3rem' }}>
                                  Errors ({errors.length})
                                </div>
                                <div style={{ display: 'grid', gap: '0.25rem' }}>
                                  {errors.map((r, i) => (
                                    <div key={i} style={{ display: 'flex', gap: '0.5rem', fontSize: 12, alignItems: 'center' }}>
                                      <span className="badge badge-error" style={{ fontSize: 10 }}>Error</span>
                                      <span style={{ fontWeight: 500 }}>{r.artist}</span>
                                      {r.message && <span className="text-muted">{r.message}</span>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // No per-run detail to show. Two lists are available and they answer
                      // different questions, so say which one is on screen: the row's badge
                      // counts the last refresh, while artists_added accumulates for the life
                      // of the playlist. Showing the latter under a badge reading "0 new" is
                      // what made this confusing.
                      const lastList = pl.last_refresh_new_artists;   // null = never recorded
                      const allList = pl.artists_added || [];
                      const canSplit = lastList != null;
                      const view = chipView[pl.id] || (canSplit ? 'last' : 'all');
                      const shown = view === 'last' ? (lastList || []) : allList;
                      const tab = (key, label) => (
                        <button key={key} className="btn btn-ghost"
                          onClick={() => setChipView(prev => ({ ...prev, [pl.id]: key }))}
                          style={{
                            fontSize: 10, padding: '2px 8px',
                            background: view === key ? 'var(--accent)' : 'none',
                            color: view === key ? '#fff' : 'var(--text-muted)',
                            borderColor: view === key ? 'var(--accent)' : 'var(--border)',
                          }}>
                          {label}
                        </button>
                      );
                      return (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                            <div className="card-title" style={{ marginBottom: 0 }}>Artists Added to Lidarr</div>
                            {canSplit && (
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {tab('last', `last refresh (${(lastList || []).length})`)}
                                {tab('all', `all time (${allList.length})`)}
                              </div>
                            )}
                            {!canSplit && (
                              <span className="text-muted" style={{ fontSize: 11 }}>all time ({allList.length})</span>
                            )}
                          </div>
                          {shown.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {shown.map((a, i) => (
                                <span key={i} className="badge badge-added">{a}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted" style={{ fontSize: 12 }}>
                              {view === 'last'
                                ? `Nothing new in the last refresh — ${allList.length} added all time.`
                                : 'No artists added yet.'}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    {(() => {
                      // Prefer live sync result unmatched, fall back to stored
                      const syncResult = syncStates[pl.id]?.result;
                      const unmatched = syncResult?.unmatched ?? pl.plex_unmatched_tracks ?? [];
                      const matchedCount = syncResult?.matched ?? pl.plex_matched_count;
                      const totalCount = syncResult?.total ?? pl.plex_total_count;
                      if (!pl.plex_playlist_id && !syncResult) return null;
                      return (
                        <div>
                          <div className="card-title" style={{ marginBottom: '0.5rem' }}>
                            Plex Match
                            {matchedCount != null && (
                              <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8 }}>
                                {matchedCount}/{totalCount} tracks
                              </span>
                            )}
                          </div>
                          {unmatched.length > 0 ? (
                            <>
                              {(() => {
                                const activeRows = unmatched.filter(t => !ignoredTracks.has(`${(t.artist||'').toLowerCase()}||${(t.title||'').toLowerCase()}`));
                                const ignoredRows = unmatched.filter(t => ignoredTracks.has(`${(t.artist||'').toLowerCase()}||${(t.title||'').toLowerCase()}`));
                                const showIgnored = expandedIgnored[pl.id];
                                const visibleRows = showIgnored ? [...activeRows, ...ignoredRows] : activeRows;
                                const activeCount = activeRows.length;
                                return (
                                  <>
                                    {activeCount > 0 && (
                                      <div className="text-muted" style={{ fontSize: 11, marginBottom: '0.4rem' }}>
                                        {activeCount} track{activeCount !== 1 ? 's' : ''} not found in Plex:
                                      </div>
                                    )}
                                    {visibleRows.length > 0 && (
                                      <table className="table" style={{ fontSize: 12 }}>
                                        <thead>
                                          <tr><th>Artist</th><th>Title</th><th>Album</th><th style={{ width: 140 }}></th></tr>
                                        </thead>
                                        <tbody>
                                          {visibleRows.map((t, i) => {
                                            const artistLower = (t.artist || '').toLowerCase();
                                            const titleLower = (t.title || '').toLowerCase();
                                            const matchKey = `${artistLower}||${titleLower}`;
                                            const isMatched = !!manualMatches[matchKey];
                                            const isIgnored = ignoredTracks.has(matchKey);
                                            const inLidarr = artistLower && (pl.artists_added || []).some(
                                              a => a.toLowerCase() === artistLower
                                            );
                                            return (
                                              <tr key={i} style={isIgnored ? { opacity: 0.4 } : undefined}>
                                                <td className="text-muted">
                                                  {t.artist && t.artist !== 'null' ? t.artist : '—'}
                                                  {!isIgnored && !inLidarr && artistLower && (
                                                    <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--red)' }}>✕ not monitored</span>
                                                  )}
                                                </td>
                                                <td>{t.title && t.title !== 'null' ? t.title : '—'}</td>
                                                <td className="text-muted">{t.album && t.album !== 'null' ? t.album : '—'}</td>
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                  {isIgnored ? (
                                                    <button
                                                      className="btn btn-ghost"
                                                      style={{ fontSize: 9, padding: '3px 8px', letterSpacing: 1 }}
                                                      onClick={() => handleUnignoreTrack(t)}
                                                    >
                                                      Unignore
                                                    </button>
                                                  ) : isMatched ? (
                                                    <span style={{ fontSize: 10, color: 'var(--green)', fontFamily: 'var(--font-mono)' }}>
                                                      ✓ matched
                                                    </span>
                                                  ) : (
                                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                      <button
                                                        className="btn btn-ghost"
                                                        style={{ fontSize: 9, padding: '3px 8px', letterSpacing: 1 }}
                                                        onClick={() => openSearchModal(t, pl.id)}
                                                      >
                                                        Search library
                                                      </button>
                                                      <button
                                                        className="btn btn-ghost"
                                                        style={{ fontSize: 9, padding: '3px 8px', letterSpacing: 1, borderColor: 'var(--border)', color: 'var(--text-muted)' }}
                                                        onClick={() => handleIgnoreTrack(t)}
                                                      >
                                                        Ignore
                                                      </button>
                                                    </div>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    )}
                                    {ignoredRows.length > 0 && (
                                      <button
                                        onClick={() => setExpandedIgnored(prev => ({ ...prev, [pl.id]: !prev[pl.id] }))}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--text-muted)', padding: '4px 0', marginTop: 4 }}
                                      >
                                        {showIgnored ? '▲' : '▼'} {ignoredRows.length} ignored
                                      </button>
                                    )}
                                    {activeCount === 0 && ignoredRows.length === 0 && null}
                                  </>
                                );
                              })()}
                              {syncResult?.lidarr_monitored?.length > 0 && (
                                <div style={{ marginTop: '0.5rem', fontSize: 11, color: 'var(--green)' }}>
                                  Triggered Lidarr search for {syncResult.lidarr_monitored.length} album{syncResult.lidarr_monitored.length !== 1 ? 's' : ''}:{' '}
                                  {syncResult.lidarr_monitored.map((m, i) => (
                                    <span key={i}>
                                      {i > 0 && ', '}
                                      <strong>{m.artist}</strong> — {m.album}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : matchedCount != null ? (
                            <div className="text-muted" style={{ fontSize: 12 }}>All tracks matched ✓</div>
                          ) : null}
                        </div>
                      );
                    })()}

                    {canRefresh && (() => {
                      const pin = pl.refresh_pin_hours;
                      const value = (pin === null || pin === undefined) ? 'adaptive' : String(Number(pin));
                      const next = nextRefreshLabel(pl);
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}
                          onClick={e => e.stopPropagation()}>
                          <span style={{ fontSize: 12 }}>Refresh</span>
                          <select
                            value={value}
                            onChange={e => handleSetCadence(e, pl, e.target.value)}
                            style={{ fontSize: 12, padding: '2px 6px', borderRadius: 4 }}>
                            <option value="adaptive">Adaptive</option>
                            <option value="1">Every 1h</option>
                            <option value="3">Every 3h</option>
                            <option value="6">Every 6h</option>
                            <option value="12">Every 12h</option>
                            <option value="24">Every 24h</option>
                            <option value="0">Never</option>
                          </select>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {cadenceLabel(pl)}{next ? ` · next ${next}` : ''}
                          </span>
                        </div>
                      );
                    })()}

                    {pl.source_url && ['url', 'm3u_url', 'listenbrainz', 'similar'].includes(pl.source_type) && (() => {
                      const effective = pl.merge_tracks !== null && pl.merge_tracks !== undefined ? !!pl.merge_tracks : globalMerge;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}
                          onClick={e => handleSetMergeTracks(e, pl, !effective)}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: effective ? 'var(--green)' : 'var(--text-muted)' }}>
                            {effective ? '✓' : '○'}
                          </span>
                          <span style={{ fontSize: 12 }}>Append tracks on refresh</span>
                          {(pl.merge_tracks === null || pl.merge_tracks === undefined) && (
                            <span className="text-muted" style={{ fontSize: 10 }}>(global default)</span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {searchModal && (
        <Suspense fallback={null}>
          <TrackSearchModal
            track={searchModal.track}
            jellyfinConfigured={jellyfinConfigured}
            navidromeConfigured={navidromeConfigured}
            onClose={() => setSearchModal(null)}
            onMatched={(key, result) => setManualMatches(prev => ({ ...prev, [key]: result }))}
          />
        </Suspense>
      )}
    </div>
  );
}
